// code taken from https://github.com/mantinedev/mantine/blob/master/packages/@mantine/hooks/src/use-debounced-value/use-debounced-value.ts
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * @typedef {Object} UseDebouncedValueOptions
 * @property {boolean} [leading]
 */

/**
 * @template T
 * @typedef {[T, () => void]} UseDebouncedValueReturnValue
 */

/**
 *
 * @template T
 * @param {T} value
 * @param {number} wait
 * @param {UseDebouncedValueOptions} [options={ leading: false }]
 * @returns {UseDebouncedValueReturnValue<T>}
 */
export function useDebouncedValue(value, wait, options = { leading: false }) {
    const [_value, setValue] = useState(value);
    const mountedRef = useRef(false);
    const timeoutRef = useRef(null);
    const cooldownRef = useRef(false);

    const cancel = useCallback(() => window.clearTimeout(timeoutRef.current), []);

    useEffect(() => {
        if (mountedRef.current) {
            if (!cooldownRef.current && options.leading) {
                cooldownRef.current = true;
                setValue(value);
            } else {
                cancel();
                timeoutRef.current = window.setTimeout(() => {
                    cooldownRef.current = false;
                    setValue(value);
                }, wait);
            }
        }
    }, [value, options.leading, wait]);

    useEffect(() => {
        mountedRef.current = true;
        return cancel;
    }, []);

    return [_value, cancel];
}
